import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { TaskTable } from '../components/TaskTable'
import { TaskDetailDialog } from '../components/TaskDetailDialog'
import { useTasks, useActiveTasks, useCancelTask, useRetryTask } from '../hooks/useTasks'
import { Task, TaskStatus } from '@/types/entities'
import { RefreshCw, Activity, CheckCircle, XCircle, Clock } from 'lucide-react'

export function TaskQueuePage() {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all')
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const [taskToCancel, setTaskToCancel] = useState<{ id: string; isRunning: boolean } | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const taskIdParam = searchParams.get('taskId')

  const { data: activeTasks, isLoading: activeLoading, refetch: refetchActive } = useActiveTasks()
  const { data: allTasks, isLoading: allLoading, refetch: refetchAll } = useTasks({
    status: statusFilter === 'all' ? undefined : statusFilter,
    limit: 100,
  })

  const cancelMutation = useCancelTask()
  const retryMutation = useRetryTask()

  const handleViewTask = (task: Task) => {
    setSelectedTaskId(task.id)
    setDialogOpen(true)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('taskId', task.id)
      return next
    }, { replace: true })
  }

  const handleCancelTask = async (taskId: string) => {
    // Find the task to check if it's running
    const task = [...(activeTasks?.data || []), ...(allTasks?.data || [])].find(t => t.id === taskId)
    const isRunning = task?.status === 'RUNNING'
    
    // Show confirmation for running tasks (with force-cancel option for stuck tasks)
    if (isRunning) {
      setTaskToCancel({ id: taskId, isRunning: true })
      setCancelConfirmOpen(true)
    } else {
      // Cancel pending tasks immediately
      await cancelMutation.mutateAsync({ taskId })
    }
  }

  const handleConfirmCancel = async (force = false) => {
    if (taskToCancel) {
      await cancelMutation.mutateAsync({ taskId: taskToCancel.id, force })
      setTaskToCancel(null)
      setCancelConfirmOpen(false)
    }
  }

  const handleRetryTask = async (taskId: string) => {
    await retryMutation.mutateAsync(taskId)
  }

  const handleRefresh = () => {
    refetchActive()
    refetchAll()
  }

  useEffect(() => {
    if (!taskIdParam) return
    setSelectedTaskId(taskIdParam)
    setDialogOpen(true)
  }, [taskIdParam])

  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('taskId')
        return next
      }, { replace: true })
    }
  }

  // Count tasks by status
  const pendingCount = allTasks?.data?.filter(t => t.status === 'PENDING').length || 0
  const runningCount = allTasks?.data?.filter(t => t.status === 'RUNNING').length || 0
  const completedCount = allTasks?.data?.filter(t => t.status === 'COMPLETED').length || 0
  const failedCount = allTasks?.data?.filter(t => t.status === 'FAILED').length || 0

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Task Queue</h1>
          <p className="text-muted-foreground">
            Monitor and manage background tasks for artwork publishing and processing.
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Pending
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{pendingCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Running
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-primary">{runningCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Completed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">{completedCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <XCircle className="h-4 w-4" />
              Failed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-destructive">{failedCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Active Tasks */}
      {(activeTasks?.data?.length || 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary animate-pulse" />
              Active Tasks
              <Badge variant="default">{activeTasks?.data?.length}</Badge>
            </CardTitle>
            <CardDescription>
              Tasks currently being processed or waiting to start.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TaskTable
              tasks={activeTasks?.data || []}
              onViewTask={handleViewTask}
              onCancelTask={handleCancelTask}
              onRetryTask={handleRetryTask}
              isLoading={activeLoading}
            />
          </CardContent>
        </Card>
      )}

      {/* All Tasks */}
      <Card>
        <CardHeader>
          <CardTitle>Task History</CardTitle>
          <CardDescription>
            View all tasks and their execution history.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" onValueChange={(v) => setStatusFilter(v as TaskStatus | 'all')}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="PENDING">Pending</TabsTrigger>
              <TabsTrigger value="RUNNING">Running</TabsTrigger>
              <TabsTrigger value="COMPLETED">Completed</TabsTrigger>
              <TabsTrigger value="FAILED">Failed</TabsTrigger>
              <TabsTrigger value="CANCELLED">Cancelled</TabsTrigger>
            </TabsList>
            <TabsContent value={statusFilter} className="mt-4">
              <TaskTable
                tasks={allTasks?.data || []}
                onViewTask={handleViewTask}
                onCancelTask={handleCancelTask}
                onRetryTask={handleRetryTask}
                isLoading={allLoading}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        taskId={selectedTaskId}
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
      />

      {/* Stop Task Confirmation Dialog */}
      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop Running Task?</AlertDialogTitle>
            <AlertDialogDescription>
              This task is currently running. Stopping it may leave the artwork in an 
              incomplete state. The task will be marked as cancelled and any partial 
              progress will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setTaskToCancel(null)}>
              Keep Running
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleConfirmCancel(false)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Stop Task
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => handleConfirmCancel(true)}
              className="bg-orange-600 text-white hover:bg-orange-700"
            >
              Force Stop (Stuck)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
